import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Check, Star, Zap } from 'lucide-react';
import TiltCard from './TiltCard';

const PricingSection = ({ pricing }) => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="tarifs" className="py-20 lg:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-semibold text-orange-400 uppercase tracking-wide">Nos Offres</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Mes formules de maintenances
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-4">
            Choisissez votre formule d'abonnement pour maintenir votre site en parfait état. Tous mes abonnements incluent maintenance, hébergement et sécurité.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-300">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span className="text-lg font-semibold">Paiement sécurisé via Stripe</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {pricing.map((plan) => (
            <TiltCard key={plan.id} glare={false} max={6} className={`h-full rounded-xl ${plan.popular ? 'md:scale-105' : ''}`}>
            <Card
              className={`relative overflow-hidden h-full transition-colors duration-300 hover:shadow-2xl bg-gray-900 ${
                plan.popular
                  ? 'border-2 border-orange-500 shadow-xl shadow-orange-500/20'
                  : 'border border-gray-700 hover:border-orange-400'
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-1 text-sm font-semibold flex items-center gap-1 rounded-bl-lg">
                  {plan.badge}
                </div>
              )}

              <CardHeader className="pt-8 pb-6">
                <CardTitle className="text-3xl font-bold text-white mb-2">
                  {plan.name}
                </CardTitle>
                <CardDescription className="text-base text-gray-400">
                  {plan.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="pb-8">
                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-5xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
                      {plan.price}€
                    </span>
                    <span className="text-gray-400 text-lg">/mois</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-4">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                        plan.popular ? 'bg-orange-500/20' : 'bg-gray-800'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          plan.popular ? 'text-orange-400' : 'text-gray-400'
                        }`} />
                      </div>
                      <span className="text-gray-300 leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="pt-6 pb-8">
                <Button
                  onClick={scrollToContact}
                  className={`w-full py-6 text-lg font-semibold rounded-full transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-lg shadow-orange-500/50 hover:shadow-xl transform hover:scale-105'
                      : 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-700'
                  }`}
                >
                  Choisir cette offre
                </Button>
              </CardFooter>
            </Card>
            </TiltCard>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm max-w-2xl mx-auto mb-6">
            * Modification de texte, photo, ou couleur — hors refonte complète de page ou ajout de fonctionnalité.
          </p>
          <p className="text-gray-400">
            Des questions sur mes offres ?{' '}
            <button
              onClick={scrollToContact}
              className="text-orange-400 hover:text-orange-300 font-semibold underline"
            >
              Contactez-moi
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
